import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  createPreviewActions,
  ShareablePreview,
  type ShareablePreviewFeeLine,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import {
  DEFAULT_CURRENCY,
  majorToMinor,
  minorToMajor,
} from "@/core/utils/func/money-minor";
import type {
  ContributionFeeQuoteResponseDto,
  ContributionInitiateRequestDto,
} from "@/features/fundraising/api/dto";
import { useContributionFeeQuote } from "@/features/fundraising/api/use-contribution-fee-quote";
import { useInitiateContribution } from "@/features/fundraising/api/use-initiate-contribution";
import { useFundraisingTranslation } from "@/features/fundraising/i18n/use-fundraising-translation";
import {
  createContributionSchema,
  type ContributionFormValues,
} from "@/features/fundraising/schemas";
import { CUSTOMER_SEGMENTS } from "@/features/shared/api/enums";
import {
  usePartySelectOptions,
  useWalletSelectOptions,
} from "@/features/shared/api";

const UUID_REGEXP =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const EMPTY_VALUES: ContributionFormValues = {
  productCode: "",
  sourceWalletId: "",
  destinationWalletId: "",
  amount: 0,
  customerSegment: "INDIVIDUAL",
  partyId: "",
  customerReference: "",
};

const toPayload = (
  values: ContributionFormValues
): ContributionInitiateRequestDto => ({
  type: "POT_CONTRIBUTION",
  productCode: values.productCode.trim(),
  sourceWalletId: values.sourceWalletId.trim(),
  destinationWalletId: values.destinationWalletId.trim(),
  destinationExternalType: "NONE",
  // Montant saisi en unité majeure → entier unité mineure (guide §4.3).
  amountMinor: majorToMinor(values.amount, DEFAULT_CURRENCY),
  currency: DEFAULT_CURRENCY,
  customerSegment: values.customerSegment,
  partyId: values.partyId?.trim() || null,
  customerReference: values.customerReference?.trim() || null,
});

/** État de la preview : contribution validée + devis calculé. */
interface ContributionPreview {
  payload: ContributionInitiateRequestDto;
  quote: ContributionFeeQuoteResponseDto;
}

/**
 * Contribution à une cagnotte (back-office, opéré pour le compte du
 * client) — `POST /Transactions/initiate` avec `type = POT_CONTRIBUTION`
 * (wallet-service §5.3, annexe A). Sans entité « collecte » côté API, la
 * création d'une cagnotte est la première contribution vers son
 * portefeuille (identifiée par `customerReference`).
 *
 * Flux : saisie → validation zod → devis de frais (`POST /fee-quotes`,
 * §6.5) → `ShareablePreview` avec section frais (règle projet) →
 * initiation idempotente → navigation vers le reçu (suivi de statut avec
 * polling, page détail des transactions).
 */
export function ContributionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useFundraisingTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createContributionSchema(), [i18n.language]);

  const feeQuote = useContributionFeeQuote();
  const initiate = useInitiateContribution();
  const [preview, setPreview] = useState<ContributionPreview | null>(null);
  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(open);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs vides.
  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload = toPayload(parsed.data);
      feeQuote.mutate(
        {
          productCode: payload.productCode,
          providerId: null,
          customerSegment: payload.customerSegment,
          feePurpose: "CUSTOMER_FEE",
          amountMinor: payload.amountMinor,
          currency: payload.currency,
          // Devis avant initiate : pas de référence wallet `FPT-…`.
          // Distinct de `customerReference` (identifiant de cagnotte).
          transactionReference: crypto.randomUUID(),
          beneficiaryPartyId: null,
          promoCode: null,
          partyId: payload.partyId ?? null,
        },
        {
          onSuccess: (quote) => setPreview({ payload, quote }),
          onError: () => notifSilenceError(t("fundraising.fees.error")),
        }
      );
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    initiate.mutate(preview.payload, {
      onSuccess: (transaction) => {
        notifSilence(t("fundraising.feedback.contributed"));
        setPreview(null);
        onOpenChange(false);
        if (transaction?.id)
          navigate(`/app/wallet/transactions/${transaction.id}`);
      },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  const requiredError = ({ value }: { value: unknown }) =>
    typeof value === "string" && value.trim()
      ? undefined
      : t("requiredFieldError");
  const uuidError = ({ value }: { value: unknown }) => {
    if (typeof value !== "string" || !value.trim()) return undefined;
    return UUID_REGEXP.test(value.trim()) ? undefined : t("invalidFieldError");
  };

  const previewFees: ShareablePreviewFeeLine[] = preview
    ? [
        {
          label: t("fundraising.fees.gross"),
          amount: minorToMajor(
            preview.quote.grossFeeAmountMinor,
            preview.quote.currency
          ),
          currency: preview.quote.currency,
        },
        ...(preview.quote.discountAmountMinor > 0
          ? [
              {
                label: t("fundraising.fees.discount"),
                amount: -minorToMajor(
                  preview.quote.discountAmountMinor,
                  preview.quote.currency
                ),
                currency: preview.quote.currency,
              },
            ]
          : []),
        {
          label: t("fundraising.fees.net"),
          amount: minorToMajor(
            preview.quote.netFeeAmountMinor,
            preview.quote.currency
          ),
          currency: preview.quote.currency,
        },
      ]
    : [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[min(56rem,calc(100%-2rem))]">
          <DialogHeader>
            <DialogTitle>{t("fundraising.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="productCode"
              validators={{ onChange: requiredError }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="productCode">
                    {t("fundraising.form.productCode")}
                  </Label>
                  <Input
                    id="productCode"
                    className="notranslate"
                    placeholder={t("fundraising.form.productCodePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="customerSegment">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fundraising.form.customerSegment")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as ContributionFormValues["customerSegment"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_SEGMENTS.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="sourceWalletId"
              validators={{ onChange: requiredError }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="sourceWalletId">
                    {t("fundraising.form.sourceWalletId")}
                  </Label>
                  <EntitySearchableSelect
                    id="sourceWalletId"
                    options={walletOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t("fundraising.form.sourceWalletIdPlaceholder")}
                    isLoading={walletsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="destinationWalletId"
              validators={{ onChange: requiredError }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="destinationWalletId">
                    {t("fundraising.form.destinationWalletId")}
                  </Label>
                  <EntitySearchableSelect
                    id="destinationWalletId"
                    options={walletOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t(
                      "fundraising.form.destinationWalletIdPlaceholder"
                    )}
                    isLoading={walletsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="amount"
              validators={{
                onChange: ({ value }) =>
                  typeof value === "number" && value > 0
                    ? undefined
                    : t("invalidFieldError"),
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="amount">{t("fundraising.form.amount")}</Label>
                  <Input
                    id="amount"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("fundraising.form.amountPlaceholder")}
                    value={field.state.value || ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="partyId" validators={{ onChange: uuidError }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="partyId">
                    {t("fundraising.form.partyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="partyId"
                    options={partyOptions}
                    value={field.state.value ?? ""}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t("fundraising.form.partyIdPlaceholder")}
                    isLoading={partiesLoading}
                    clearable
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="customerReference">
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="customerReference">
                    {t("fundraising.form.customerReference")}
                  </Label>
                  <Input
                    id="customerReference"
                    className="notranslate"
                    placeholder={t(
                      "fundraising.form.customerReferencePlaceholder"
                    )}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={feeQuote.isPending || initiate.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={feeQuote.isPending || initiate.isPending}
              >
                {feeQuote.isPending ? <ButtonSpinner /> : null}
                {feeQuote.isPending
                  ? t("fundraising.fees.pending")
                  : t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview.payload}
          title={t("fundraising.preview.title")}
          description={
            preview.quote.noActiveRule
              ? `${t("fundraising.preview.description")} ${t("fundraising.fees.noActiveRule")}`
              : t("fundraising.preview.description")
          }
          mode="user"
          // Section frais obligatoire (cahier §9.5) : devis fee-engine
          // calculé AVANT la preview.
          fees={previewFees}
          total={{
            label: t("fundraising.preview.totalLabel"),
            amount: minorToMajor(
              preview.payload.amountMinor + preview.quote.netFeeAmountMinor,
              preview.payload.currency
            ),
            currency: preview.payload.currency,
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !initiate.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t: (key) => t(key as string),
            confirmLoading: initiate.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
